/**
 * TeamSystem Impact - Anno di lavoro preso dall'indirizzo
 *
 * Le notifiche mandano link con `?year=NNNN` (es. «ti è stato assegnato il
 * datapoint X»), ma l'anno di lavoro vive in uno store globale e nessuno
 * leggeva quel parametro: chi aveva selezionato un altro anno cliccava la
 * notifica, atterrava sulla pagina giusta e non ci trovava niente — lo stesso
 * sintomo di una funzione rotta.
 *
 * Quattro cose imparate da due giri di revisione indipendente, che valgono più
 * del codice che seguono:
 *
 * 1. **Non si inventano anni.** La prima versione aggiungeva l'anno del link
 *    all'elenco. Ma `useSyncAvailableYears` gira nello stesso layout, è
 *    asincrona e arriva dopo: rimpiazza l'elenco con quello del server e, se
 *    l'anno corrente non c'è più, ripiega sul più recente. Partendo dal 2026 e
 *    aprendo `?year=7777` si finiva sul 10000 — un TERZO anno, né quello
 *    chiesto né quello di prima.
 *
 * 2. **L'elenco del browser non è una fonte attendibile.** È persistito, quindi
 *    può contenere anni cancellati lato server da un pezzo. Decidere su quello
 *    riporta allo stesso terzo anno per un'altra strada: si applica l'anno, si
 *    consuma il parametro, e mezzo secondo dopo la sincronizzazione sposta
 *    altrove senza più niente da cui recuperare. Perciò qui non si conclude
 *    niente finché il server non ha confermato l'elenco (`yearsSynced`).
 *
 * 3. **Il parametro si consuma**, ma solo dopo essere stato applicato: se
 *    restasse, una ricarica o il tasto Indietro riporterebbero all'anno del link
 *    scavalcando la scelta fatta a mano col selettore.
 *
 * 4. **Il layout non si rimonta mai.** È l'elemento della route padre di tutte
 *    le pagine protette: si naviga dentro l'app senza ricaricarlo. Quindi la
 *    memoria di «questa richiesta l'ho già servita» va azzerata quando il
 *    parametro sparisce, altrimenti la SECONDA notifica dello stesso anno non
 *    sposterebbe più niente — e sarebbe di nuovo la pagina vuota di partenza.
 */

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWorkingYearStore } from './workingYearStore';

export function useYearFromUrl() {
  const [searchParams, setSearchParams] = useSearchParams();
  const anno = searchParams.get('year');
  const availableYears = useWorkingYearStore((s) => s.availableYears);
  const yearsSynced = useWorkingYearStore((s) => s.yearsSynced);
  // Quale richiesta abbiamo già servito. Senza, ogni volta che l'elenco degli
  // anni cambia riporteremmo l'utente all'anno del link anche dopo che ha
  // scelto altro dal selettore.
  const giaApplicato = useRef<string | null>(null);

  useEffect(() => {
    // Sparito il parametro, la richiesta è chiusa: si riparte puliti, così la
    // notifica successiva — anche se punta allo stesso anno — viene servita.
    if (!anno) {
      giaApplicato.current = null;
      return;
    }
    if (giaApplicato.current === anno) return;

    // Finché l'elenco è quello rimasto nel browser non si decide niente: né si
    // applica, né si consuma il parametro. Questo effetto rigira quando il
    // server risponde.
    if (!yearsSynced) return;

    // Da qui in giù la decisione è definitiva: l'elenco è quello vero.
    giaApplicato.current = anno;

    const valore = Number(anno);
    // Un anno non numerico o assurdo si ignora in silenzio: meglio restare
    // dov'eravamo che spostare il selettore su un valore inventato.
    if (!Number.isInteger(valore) || valore < 1900 || valore > 9999999) return;
    // Un anno che l'azienda non ha (mai esistito, o archiviato) non è un posto
    // dove portare qualcuno. Il parametro resta nell'indirizzo: non è stato
    // consumato perché non è stato usato.
    if (!availableYears.includes(valore)) return;

    const { year, setYear } = useWorkingYearStore.getState();
    if (valore !== year) setYear(valore);

    setSearchParams(
      (precedenti) => {
        const puliti = new URLSearchParams(precedenti);
        puliti.delete('year');
        return puliti;
      },
      { replace: true }
    );
  }, [anno, availableYears, yearsSynced, setSearchParams]);
}
